import { Box, type SxProps } from '@mui/material';

type Props = {
  additionalStyles?: SxProps;
  color?: string;
  gap?: `${number}px` | `${number}rem`;
  testid?: string;
  size?: `${number}px` | `${number}rem` | { xs: `${number}px`; md: `${number}px` };
  value?: number;
};

const emptyAndFilledCommonStyles = {
  content: '""',
  position: 'absolute',
  inset: 0,
  WebkitMaskRepeat: 'repeat-x',
  maskRepeat: 'repeat-x',
  WebkitMaskSize:
    'calc(var(--star-rating-size) + var(--star-rating-gap) - 0.5px) var(--star-rating-size)',
  maskSize:
    'calc(var(--star-rating-size) + var(--star-rating-gap) - 0.5px) var(--star-rating-size)',
  WebkitMaskPosition: 'left center',
  maskPosition: 'left center',
};

export function Rating({
  additionalStyles,
  color = '#008254',
  gap = '0.15rem',
  testid,
  size = '24px',
  value,
}: Props) {
  if (!value) return null;
  return (
    <Box
      component="span"
      role="img"
      data-testid={testid}
      aria-label={`${value} out of 5`}
      sx={{
        '--empty-star-svg': `url("data:image/svg+xml;utf8, <svg xmlns='http://www.w3.org/2000/svg' viewBox='2 2 20 19'><path d='M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z'/></svg>")`,
        '--star-svg': `url("data:image/svg+xml;utf8, <svg xmlns='http://www.w3.org/2000/svg' viewBox='2 2 20 19'><path d='M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z'/></svg>")`,
        '--value': value,
        '--star-fill': color,
        '--star-rating-gap': gap,
        '--star-rating-size': size,
        '--star-rating-max': 5,
        '--star-empty': '#ccc',
        '--pct': 'calc(((var(--value) * 100%) / var(--star-rating-max)) + 1px)',
        position: 'relative',
        display: 'inline-block',
        inlineSize: `calc(
          var(--star-rating-size) * var(--star-rating-max) +
            (max(var(--star-rating-max) - 1, 0) * var(--star-rating-gap))
        )`,
        blockSize: 'var(--star-rating-size)',
        lineHeight: 1,
        fontSize: 'var(--star-rating-size)',
        fontFamily: 'system-ui, sans-serif',
        '&:before': {
          background: 'var(--star-empty)',
          WebkitMaskImage: 'var(--empty-star-svg)',
          maskImage: 'var(--empty-star-svg)',
          ...emptyAndFilledCommonStyles,
        },
        '&:after': {
          background: 'var(--star-fill)',
          inlineSize: 'var(--pct)',
          overflow: 'hidden',
          WebkitMaskImage: 'var(--star-svg)',
          maskImage: 'var(--star-svg)',
          ...emptyAndFilledCommonStyles,
        },
        '@supports not (mask-image: var(--star-svg))': {
          '&:before': {
            content: '"★★★★★"',
            color: 'var(--star-empty)',
            letterSpacing: 'var(--star-rating-gap)',
          },
          '&:after': {
            content: '"★★★★★"',
            color: 'var(--star-fill)',
            letterSpacing: 'var(--star-rating-gap)',
            position: 'absolute',
            inset: 0,
            inlineSize: 'var(--pct)',
            overflow: 'hidden',
          },
        },
        '@media (forced-colors: active)': {
          '&:before': {
            background: 'CanvasText',
          },
          '&:after': {
            background: 'Highlight',
          },
        },
        ...additionalStyles,
      }}
    />
  );
}
